import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FiCheck, FiClock } from 'react-icons/fi';

/** Максимум: 30 суток (совпадает с сервером). */
export const MAX_DURATION_MINUTES = 30 * 24 * 60;

const DAYS = Array.from({ length: 31 }, (_, i) => String(i)); // 0–30
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

export function minutesToParts(total) {
  const n = Math.max(0, Math.round(Number(total) || 0));
  const days = Math.min(30, Math.floor(n / (24 * 60)));
  const rem = n - days * 24 * 60;
  const hours = Math.floor(rem / 60);
  const minutes = rem % 60;
  return { days, hours, minutes };
}

export function partsToMinutes(days, hours, minutes) {
  const d = Math.max(0, Math.min(30, Math.round(Number(days) || 0)));
  const h = Math.max(0, Math.min(23, Math.round(Number(hours) || 0)));
  const m = Math.max(0, Math.min(59, Math.round(Number(minutes) || 0)));
  return Math.min(MAX_DURATION_MINUTES, d * 24 * 60 + h * 60 + m);
}

export function formatDurationLabel(totalMinutes) {
  const n = Math.round(Number(totalMinutes) || 0);
  if (!n || n <= 0) return 'Без таймера';
  const { days, hours, minutes } = minutesToParts(n);
  const bits = [];
  if (days) bits.push(`${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}`);
  if (hours) bits.push(`${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'}`);
  if (minutes) bits.push(`${minutes} мин`);
  return bits.join(' ') || 'Без таймера';
}

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: ${({ $compact }) => ($compact ? '110px' : '160px')};
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.12)' : 'rgba(255,255,255,.12)')};
  background: ${({ $light, $has }) => {
    if ($has) return 'rgba(56,182,255,.14)';
    return $light ? '#f4f7fb' : 'rgba(0,0,0,.25)';
  }};
  color: inherit;
  font: inherit;
  font-weight: 700;
  font-size: .85rem;
  cursor: pointer;
  text-align: left;
  &:hover { border-color: rgba(56,182,255,.45); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 400000;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  padding: 16px;
`;

const Popover = styled.div`
  width: min(360px, 100%);
  border-radius: 18px;
  padding: 16px;
  background: var(--solid, #101828);
  color: var(--txt, #eaf2fb);
  border: 1px solid var(--stroke, rgba(255,255,255,.09));
  box-shadow: 0 24px 60px rgba(0,0,0,.45);
`;

const WheelRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin: 10px 0 12px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    text-align: center;
    font-size: .72rem;
    font-weight: 800;
    opacity: 0.55;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const Wheel = styled.div`
  height: 168px;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  border-radius: 12px;
  border: 1px solid var(--stroke, rgba(255,255,255,.09));
  background: rgba(0,0,0,.18);
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(56,182,255,.35); border-radius: 4px; }
`;

const WheelItem = styled.button`
  display: block;
  width: 100%;
  height: 42px;
  scroll-snap-align: center;
  border: 0;
  background: ${({ $on }) => ($on ? 'rgba(56,182,255,.22)' : 'transparent')};
  color: ${({ $on }) => ($on ? '#38b6ff' : 'inherit')};
  font: inherit;
  font-weight: ${({ $on }) => ($on ? 900 : 600)};
  font-size: 1.05rem;
  cursor: pointer;
  &:hover { background: rgba(56,182,255,.12); }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  button {
    flex: 1;
    border: 0;
    border-radius: 10px;
    padding: 10px 12px;
    font-weight: 800;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
`;

function ScrollCol({ label, items, value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const i = items.findIndex((x) => Number(x) === Number(value));
    const target = el.children[i >= 0 ? i : 0];
    if (target) {
      el.scrollTop = target.offsetTop - el.clientHeight / 2 + target.clientHeight / 2;
    }
  }, [items, value]);

  return (
    <Col>
      <label>{label}</label>
      <Wheel ref={ref}>
        {items.map((it) => (
          <WheelItem
            key={it}
            type="button"
            $on={Number(it) === Number(value)}
            onClick={() => onChange(Number(it))}
          >
            {it}
          </WheelItem>
        ))}
      </Wheel>
    </Col>
  );
}

/**
 * Выбор лимита таймера: дни / часы / минуты (колёса прокрутки).
 * value — минуты (строка/число) или '' для «без таймера».
 */
export default function DurationPicker({
  value,
  onChange,
  light = false,
  compact = false,
  placeholder = 'Лимит времени',
}) {
  const [open, setOpen] = useState(false);
  const total = Number(value) > 0 ? Number(value) : 0;
  const init = minutesToParts(total || 60);
  const [days, setDays] = useState(init.days);
  const [hours, setHours] = useState(init.hours);
  const [minutes, setMinutes] = useState(init.minutes);

  useEffect(() => {
    if (!open) return;
    const p = minutesToParts(Number(value) > 0 ? Number(value) : 60);
    setDays(p.days);
    setHours(p.hours);
    setMinutes(p.minutes);
  }, [open, value]);

  const preview = partsToMinutes(days, hours, minutes);
  const label = total > 0 ? formatDurationLabel(total) : placeholder;

  return (
    <>
      <Trigger
        type="button"
        $light={light}
        $has={total > 0}
        $compact={compact}
        onClick={() => setOpen(true)}
        title="Лимит времени на выполнение с момента «В работу»"
      >
        <FiClock />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </Trigger>

      {open && ReactDOM.createPortal(
        <Overlay onClick={() => setOpen(false)}>
          <Popover
            onClick={(e) => e.stopPropagation()}
            style={{
              ['--solid']: light ? '#fff' : '#101828',
              ['--txt']: light ? '#17233b' : '#eaf2fb',
              ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
            }}
          >
            <div style={{ textAlign: 'center', fontWeight: 900, color: '#38b6ff', marginBottom: 4 }}>
              Время на выполнение
            </div>
            <div style={{ textAlign: 'center', fontSize: '.78rem', opacity: 0.65, marginBottom: 4, lineHeight: 1.4 }}>
              Прокрутите столбцы: дни, часы, минуты
            </div>
            <WheelRow>
              <ScrollCol label="Дни" items={DAYS} value={days} onChange={setDays} />
              <ScrollCol label="Часы" items={HOURS} value={hours} onChange={setHours} />
              <ScrollCol label="Минуты" items={MINUTES} value={minutes} onChange={setMinutes} />
            </WheelRow>
            <div style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.15rem', marginBottom: 12 }}>
              {preview > 0 ? formatDurationLabel(preview) : 'Выберите значение'}
            </div>
            <Actions>
              <button
                type="button"
                style={{ background: light ? '#e2e8f0' : 'rgba(255,255,255,.1)', color: light ? '#0f172a' : '#e2e8f0' }}
                onClick={() => { onChange(''); setOpen(false); }}
              >
                Без таймера
              </button>
              <button
                type="button"
                style={{
                  background: 'linear-gradient(135deg,#38b6ff,#2488d8)',
                  color: '#fff',
                  opacity: preview > 0 ? 1 : 0.45,
                }}
                disabled={preview <= 0}
                onClick={() => {
                  if (preview <= 0) return;
                  onChange(String(preview));
                  setOpen(false);
                }}
              >
                <FiCheck /> Готово
              </button>
            </Actions>
          </Popover>
        </Overlay>,
        document.body
      )}
    </>
  );
}
